import {
  Box,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { tierTitleKey } from "src/business/subscription/tierLabel";
import { formatPrice } from "src/Utils/number";
import { AdminPaymentDto } from "src/data/admin/types/payment";
import {
  colorActionDanger,
  colorTextError,
  colorTextMuted,
  colorTextSuccess,
} from "src/design/tokens";
import { PaymentStatusChip } from "src/components/Main/Pages/Payments/components/PaymentStatusChip";

interface AdminPaymentRowProps {
  payment: AdminPaymentDto;
  onPreviewProof: (paymentId: string) => void;
  onConfirm: (payment: AdminPaymentDto) => void;
  onReject: (payment: AdminPaymentDto) => void;
}

/**
 * One payment in the ops queue. The decision controls exist only while it is
 * PENDING — the backend refuses a decision on anything else with a 409, so
 * offering them on a resolved row would only produce a refusal.
 */
export const AdminPaymentRow = ({
  payment,
  onPreviewProof,
  onConfirm,
  onReject,
}: AdminPaymentRowProps) => {
  const { t, i18n } = useTranslation();
  const isPending = payment.status === "PENDING";

  return (
    <TableRow hover>
      <TableCell sx={{ pl: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {payment.restaurantName}
        </Typography>
        <Typography variant="caption" sx={{ color: colorTextMuted }}>
          {dayjs(payment.createdAt).format("MMM D, YYYY")}
        </Typography>
      </TableCell>
      <TableCell>{t(`payments.type.${payment.type}`)}</TableCell>
      <TableCell>
        {t(tierTitleKey(payment.targetTier), {
          defaultValue: payment.targetTier,
        })}
      </TableCell>
      <TableCell>
        {t(`payments.period.${payment.period}`)}
        <Typography variant="caption" sx={{ display: "block", color: colorTextMuted }}>
          {t("payments.months", { count: payment.months })}
        </Typography>
      </TableCell>
      <TableCell>
        {t("payments.amount", {
          amount: formatPrice(payment.amount, i18n.language),
        })}
      </TableCell>
      <TableCell>
        <PaymentStatusChip status={payment.status} />
        {payment.rejectionReason && (
          <Typography
            variant="caption"
            sx={{ display: "block", color: colorTextError }}
          >
            {t("payments.history.rejectionReason", {
              reason: payment.rejectionReason,
            })}
          </Typography>
        )}
        {payment.resolvedBy && (
          <Typography
            variant="caption"
            sx={{ display: "block", color: colorTextMuted }}
          >
            {t("payments.admin.resolvedBy", { admin: payment.resolvedBy })}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Tooltip title={t("payments.admin.actions.preview")}>
            <IconButton
              size="small"
              onClick={() => onPreviewProof(payment.id)}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {isPending && (
            <>
              <Tooltip title={t("payments.admin.actions.confirm")}>
                <IconButton
                  size="small"
                  onClick={() => onConfirm(payment)}
                  sx={{ color: colorTextSuccess }}
                >
                  <CheckCircleOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("payments.admin.actions.reject")}>
                <IconButton
                  size="small"
                  onClick={() => onReject(payment)}
                  sx={{ color: colorActionDanger }}
                >
                  <HighlightOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};
